/**
 * Routing policies + caller blocklist (Prompt 2.5, design §10.2, D11, D13,
 * D21, D22). Business hours / holidays in the policy timezone, after-hours /
 * overflow / fallback actions, transfer destinations (usable once verified by
 * a call), recording, concurrency cap and anonymous-caller handling; the
 * simulator answers "what happens to a call at this time from this number?".
 */
import { del, get, patch, post, upload } from '@core/api/client';
import { endpoints } from '@core/api/endpoints';
import { ApiError, isApiError } from '@core/api/types';

export interface TimeWindow {
  start: string;
  end: string;
}
export interface WeeklyWindow extends TimeWindow {
  /** 0 = Sunday … 6 = Saturday. */
  day: number;
}
export interface Holiday {
  /** YYYY-MM-DD in the policy timezone. */
  date: string;
  label?: string;
  /** Open hours that day; absent = closed all day. */
  windows?: TimeWindow[];
}

export type PolicyAction =
  | { action: 'connect_agent'; agentId: string }
  | { action: 'forward'; destinationLabel: string }
  | { action: 'voicemail' }
  | { action: 'message_hangup'; text: string }
  | Record<string, never>;

export type VerificationStatus = 'unverified' | 'pending' | 'verified';

export interface TransferDestinationView {
  label: string;
  kind: 'pstn' | 'sip';
  e164: string | null;
  sip: string | null;
  /** L.2: cold (default) or warm transfer (absent on older APIs = cold). */
  mode?: 'cold' | 'warm';
  /** L.5: SIP destinations — dial (default) or REFER; null for phone numbers. */
  sipMode?: 'dial' | 'refer' | null;
  hours: WeeklyWindow[];
  verification: {
    status: VerificationStatus;
    requestedAt: string | null;
    verifiedAt: string | null;
    expiresAt: string | null;
  };
}

/** Recording is never on without the consent announcement (D14). */
export type RecordingMode = 'off' | 'consent';

export interface ConsentAudio {
  /** `tts`: generated voice; `upload`: tenant's file; `say`: read by the phone system. */
  source: 'tts' | 'upload' | 'say';
  ready: boolean;
  voice: 'agent' | 'default' | null;
  text: string;
}
export type AnonymousCallerPolicy = 'allow' | 'voicemail' | 'reject';

export interface RoutingPolicy {
  id: string;
  name: string;
  timezone: string;
  weeklyHours: WeeklyWindow[];
  holidays: Holiday[];
  afterHours: PolicyAction;
  overflow: PolicyAction;
  fallback: PolicyAction;
  transfer: { enabled: boolean; destinations: TransferDestinationView[] };
  recordingMode: RecordingMode;
  consentText: string | null;
  /** What callers hear before the agent when recording is on (2.6). */
  consent: ConsentAudio;
  maxConcurrentCalls: number | null;
  anonymousCallers: AnonymousCallerPolicy;
  version: number;
  createdAt: string;
  updatedAt: string;
  numbers: Array<{ id: string; e164: string; label: string | null }>;
}

export interface PolicyIssue {
  field: string;
  message: string;
}

export interface RoutingPolicySaveResult extends RoutingPolicy {
  warnings: PolicyIssue[];
  recompiled: number;
}

/** Write shape: destinations as { label, e164 | sip, hours? }. */
export interface RoutingPolicyPayload {
  name?: string;
  timezone?: string;
  weeklyHours?: WeeklyWindow[];
  holidays?: Holiday[];
  afterHours?: PolicyAction;
  overflow?: PolicyAction;
  fallback?: PolicyAction;
  transfer?: {
    enabled: boolean;
    destinations: Array<{
      label: string;
      e164?: string;
      sip?: string;
      mode?: 'cold' | 'warm';
      sipMode?: 'dial' | 'refer';
      hours?: WeeklyWindow[];
    }>;
  };
  recordingMode?: RecordingMode;
  consentText?: string | null;
  maxConcurrentCalls?: number | null;
  anonymousCallers?: AnonymousCallerPolicy;
}

export interface RoutingPolicyOptions {
  launchCountries: string[];
  planMaxConcurrent: number | null;
  anonymousCallerPolicies: AnonymousCallerPolicy[];
  recordingModes: RecordingMode[];
}

export type RouteDecision =
  | {
      action: 'connect_engine';
      agentId: string;
      engine: string;
      reason: 'ok' | 'after_hours' | 'holiday';
    }
  | {
      action: 'forward';
      destination: string;
      message: string | null;
      reason: string;
    }
  | { action: 'voicemail'; message: string | null; reason: string }
  | { action: 'message_and_hangup'; message: string; reason: string }
  | { action: 'reject'; reason: string };

export interface RouteStep {
  step:
    'number' | 'blocklist' | 'anonymous' | 'service' | 'hours' | 'agent' | 'capacity' | 'action';
  outcome: 'pass' | 'stop';
  detail: string;
}

export interface SimulationResult {
  decision: RouteDecision;
  explanation: string[];
  trace: RouteStep[];
  local: { date: string; time: string; day: number; timezone: string };
  open: boolean;
  basis: 'always' | 'weekly' | 'holiday';
  holiday: string | null;
  caller: string | null;
  number: { id: string; e164: string; label: string | null } | null;
  policyVersion: number;
}

export interface SimulatePayload {
  at?: string;
  callerE164?: string | null;
  numberId?: string;
  agentId?: string;
  activeCalls?: number;
}

export interface BlockedCaller {
  id: string;
  e164: string;
  reason: string | null;
  createdBy: string | null;
  createdAt: string;
}

/** Transfers to one destination, last 30 days (Prompt 3.5). */
export interface DestinationUsage {
  label: string;
  attempts: number;
  answered: number;
  lastUsedAt: string | null;
}

export const routingPoliciesService = {
  options(): Promise<RoutingPolicyOptions> {
    return get<RoutingPolicyOptions>(endpoints.routingPolicies.options);
  },
  list(): Promise<RoutingPolicy[]> {
    return get<RoutingPolicy[]>(endpoints.routingPolicies.list);
  },
  get(id: string): Promise<RoutingPolicy> {
    return get<RoutingPolicy>(endpoints.routingPolicies.byId(id));
  },
  create(payload: RoutingPolicyPayload): Promise<RoutingPolicySaveResult> {
    return post<RoutingPolicySaveResult>(endpoints.routingPolicies.list, payload);
  },
  update(id: string, payload: RoutingPolicyPayload): Promise<RoutingPolicySaveResult> {
    return patch<RoutingPolicySaveResult>(endpoints.routingPolicies.byId(id), payload);
  },
  remove(id: string): Promise<{ deleted: true }> {
    return del<{ deleted: true }>(endpoints.routingPolicies.byId(id));
  },
  simulate(id: string, payload: SimulatePayload): Promise<SimulationResult> {
    return post<SimulationResult>(endpoints.routingPolicies.simulate(id), payload);
  },
  /** Places the verification call that reads a 6-digit code. */
  verifyDestination(id: string, label: string): Promise<RoutingPolicy> {
    return post<RoutingPolicy>(endpoints.routingPolicies.verify(id, label), {});
  },
  /** 3.5: transfers per destination over the last 30 days. */
  destinationUsage(id: string): Promise<DestinationUsage[]> {
    return get<DestinationUsage[]>(endpoints.routingPolicies.destinationUsage(id));
  },
  /** 3.5: a short test call to a verified destination. */
  testCallDestination(id: string, label: string): Promise<{ placed: true; label: string }> {
    return post<{ placed: true; label: string }>(endpoints.routingPolicies.testCall(id, label), {});
  },
  confirmDestination(id: string, label: string, code: string): Promise<RoutingPolicy> {
    return post<RoutingPolicy>(endpoints.routingPolicies.confirm(id, label), {
      code,
    });
  },
  /** MP3 of the consent announcement in the voice callers will hear. */
  consentPreview(id: string, text?: string): Promise<Blob> {
    return post<Blob>(
      endpoints.routingPolicies.consentPreview(id),
      { text },
      { responseType: 'blob' },
    ).catch(async (err: unknown) => {
      throw await readBlobError(err);
    });
  },
  /** Replace the generated announcement with a recorded MP3 (≤ 2 MB). */
  uploadConsentAudio(id: string, file: File): Promise<RoutingPolicy> {
    const form = new FormData();
    form.append('file', file);
    return upload<RoutingPolicy>(endpoints.routingPolicies.consentAudio(id), form);
  },
  useGeneratedConsent(id: string): Promise<RoutingPolicy> {
    return del<RoutingPolicy>(endpoints.routingPolicies.consentAudio(id));
  },
} as const;

export const callerBlocklistService = {
  list(): Promise<BlockedCaller[]> {
    return get<BlockedCaller[]>(endpoints.callerBlocklist.list);
  },
  add(e164: string, reason?: string | null): Promise<BlockedCaller> {
    return post<BlockedCaller>(endpoints.callerBlocklist.list, {
      e164,
      reason: reason ?? null,
    });
  },
  remove(e164: string): Promise<{ removed: true }> {
    return del<{ removed: true }>(endpoints.callerBlocklist.byE164(e164));
  },
} as const;

/** Blob responses carry their JSON error in a Blob: decode the message. */
async function readBlobError(err: unknown): Promise<unknown> {
  if (!isApiError(err) || !(err.details instanceof Blob)) return err;
  try {
    const data = JSON.parse(await err.details.text()) as { message?: unknown; code?: unknown };
    return new ApiError(
      err.status,
      typeof data.code === 'string' ? data.code : 'UNKNOWN_ERROR',
      typeof data.message === 'string' ? data.message : err.message,
      data,
    );
  } catch {
    return err;
  }
}
